import { useState } from "react"
import { useCheckIfScannedFoodImageChanged } from "features/meal/hooks/useScannedImageUploader"
import { formatInputtingFoodName } from "utils/formater"
import { IngredientOptionType } from "model/food/types"
import { hasIngredientFoods } from "model/food/utils/typeGuards/foodTypeGuards"
import { checkIsIngredientsChanged } from "features/meal/hooks/helper/checkIsMealChanged"
import { IAIScanFoodOption } from "model/food/types/AIScan.types"
import {
  getNutritionFromIngredients,
  updateIngredientsWhenPortionChange,
} from "features/food/helper/myFoodForm"
import { v4 as uuidv4 } from "uuid"
import {
  IEdamaFoodSearchResult,
  IEdamaIngredientOption,
} from "model/food/types/edama.types"
import { isFoodInfoChanged } from "features/food/helper/foodHelper"
import { updateManualNutritionMealWhenPortionChange } from "features/food/helper/edamaForm"

const isScannedMealFoodChanged = (
  initFood: IAIScanFoodOption,
  food: IAIScanFoodOption,
) => {
  const isScannedMealFoodInfoChanged = isFoodInfoChanged(initFood, food)

  if (isScannedMealFoodInfoChanged) {
    return true
  }

  let isIngredientChanged = false

  if (hasIngredientFoods(food) && hasIngredientFoods(initFood)) {
    isIngredientChanged = checkIsIngredientsChanged(
      initFood.ingredients,
      food.ingredients,
    )
  }

  return isIngredientChanged
}

const useAIScanFoodForm = (initFood: IAIScanFoodOption) => {
  const [food, setFood] = useState<IAIScanFoodOption>({ ...initFood })

  const isImageChanged = useCheckIfScannedFoodImageChanged(initFood.id)

  const onNameChange = (name: string) => {
    const formattedName = formatInputtingFoodName(name)
    setFood({ ...food, name: formattedName })
  }

  const onPortionChange = (portion: number) => {
    if (!portion) return

    const nextIngredients = updateIngredientsWhenPortionChange({
      ingredients: food.ingredients,
      portion: food.portion,
      nextPortion: portion,
    })

    const nutrition = food.isManualNutrition
      ? updateManualNutritionMealWhenPortionChange(food, portion)
      : getNutritionFromIngredients(nextIngredients)
    setFood({ ...food, ingredients: nextIngredients, ...nutrition, portion })
  }

  const onUnitChange = (unit: string) => {
    setFood({ ...food, unit })
  }

  const onAddIngredient = ({ id, ...ingredient }: IEdamaFoodSearchResult) => {
    const addedIngredient: IEdamaIngredientOption = {
      ...ingredient,
      foodId: id,
      uuid: uuidv4(),
    }

    const updatedIngredients = [...food.ingredients, addedIngredient]
    const nutrition = food.isManualNutrition
      ? {}
      : getNutritionFromIngredients(updatedIngredients)

    setFood({ ...food, ingredients: updatedIngredients, ...nutrition })
  }

  const onDeleteIngredient = (uuid: string) => {
    const updatedIngredients = food.ingredients.filter(
      (ingredient) => ingredient.uuid !== uuid,
    )

    const nutrition = food.isManualNutrition
      ? {}
      : getNutritionFromIngredients(updatedIngredients)

    setFood({ ...food, ingredients: updatedIngredients, ...nutrition })
  }

  const onUpdateIngredient = (updatedIngredient: IngredientOptionType) => {
    const updatedIngredients = food.ingredients.map((ingredient) =>
      updatedIngredient.uuid === ingredient.uuid
        ? updatedIngredient
        : ingredient,
    )

    const nutrition = food.isManualNutrition
      ? {}
      : getNutritionFromIngredients(updatedIngredients)
    setFood({ ...food, ingredients: updatedIngredients, ...nutrition })
  }

  const isFoodChanged = () => {
    return isImageChanged || isScannedMealFoodChanged(initFood, food)
  }

  const updateFoodAsManualNutrition = ({
    gram,
    ...nutrition
  }: Partial<IAIScanFoodOption>) => {
    if (food) {
      setFood({ ...food, ...nutrition, isManualNutrition: true })
    }
  }

  return {
    food,
    onNameChange,
    onPortionChange,
    onUnitChange,
    onAddIngredient,
    onDeleteIngredient,
    onUpdateIngredient,
    isFoodChanged,
    updateFoodAsManualNutrition,
  }
}

export default useAIScanFoodForm
